import type {ReactElement} from 'react';
import {useRef} from 'react';
import React from 'react';
import {Button, Size, Type} from '@Components/button';
import {addNewPage} from '@Components/poster-editor/library/poster-editor-page-library';
import styles from './add-new-page-button.module.scss';
import {useGetNumberOfPages} from '@Hooks/poster-editor/useGetNumberOfPages';
import {PREMIUM_LIMIT_FOR_NUM_PAGES} from '@PosterWhiteboard/poster/poster.class';
import {Tooltip} from '@Components/tooltip';
import {Text, TextSize} from '@Components/text';
import {useAppSelector} from '@/hooks';
import {useShowContextualSkeletons} from '@Hooks/poster-editor/useShowContextualSkeletons';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import {AddPageGA4EventParams, GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

export function AddNewPageButton(): ReactElement<any> | null {
  const numPages = useGetNumberOfPages();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const isMultiPageEnabled = useAppSelector((state) => {
    return state.userPreferences.isMultiPageEnabled;
  });
  const isPosterLoading = useShowContextualSkeletons();

  if (!isMultiPageEnabled || isPosterLoading || isEditorMobileVariant()) {
    return null;
  }

  function onClick(): void {
    trackPosterBuilderGA4Events(GA4EventName.ADD_PAGE, {
      [GA4EventParamName.TYPE]: AddPageGA4EventParams.PAGE_BOTTOM_BUTTON,
    });
    addNewPage().catch((e) => {
      console.error(e);
    });
  }

  const onMouseEnter = (): void => {
    if (numPages < PREMIUM_LIMIT_FOR_NUM_PAGES) {
      return;
    }

    setShowTooltip(true);
  };
  const onMouseLeave = (): void => {
    if (showTooltip) {
      setShowTooltip(false);
    }
  };

  return (
    <>
      <div className={styles.container} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} ref={containerRef}>
        {showTooltip ? (
          <Tooltip className={styles.tooltip} id="max-limit-tooltip" text={<Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_max_page_limit_message')} />} />
        ) : null}
        <Button
          disabled={numPages >= PREMIUM_LIMIT_FOR_NUM_PAGES}
          onClick={onClick}
          icon="icon-plus"
          type={Type.DASHED}
          size={Size.SMALL}
          customClasses={`spacing-m-t-3 ${styles.button}`}
          text={window.i18next.t('pmwjs_add_page')}
        />
      </div>
    </>
  );
}
